import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import JvrisLoading from "../../../../../../../components/JvrisLoading";
import { useEditorContext } from "../../../context/EditorContext";
import { usePecasContext } from "../../../context/PecasContext";
import { ModelTypeSelectI } from "../../../context/PecasContext/interfaces";
import { desformatarPeca } from "../../../context/PecasContext/utils/pecaHtml";
import * as S from "../../../styled";

const InputsSection = () => {
  const {
    modelos,
    updateCurrModel,
    prepareModelList,
    prepareModelListUser,
    updateModelTypeFilterValue,
    modelTypeFilter,
    prepareModelTypeList,
    getModeloById,
    modelTitle,
    tiposProcessoPJE,
    updateTitle,
    currModel,
    loadingModelosPecas,
    loadingTiposProcessoPJE,
    recPeca,
    finlPeca,
  } = usePecasContext();
  const { text, updateText } = useEditorContext();
  const [selectedAllModelId, setSelectedAllModelId] = useState<number | null>(
    null,
  );
  const [selectedMyModelId, setSelectedMyModelId] = useState<number | null>(
    null,
  );
  const [hasModelSelectionInteraction, setHasModelSelectionInteraction] =
    useState(false);
  const modeloRotaAplicadoRef = useRef(false);
  const modeloSelecionadoAplicadoRef = useRef<number | null>(null);
  const textRef = useRef(text);

  useEffect(() => {
    textRef.current = text;
  }, [text]);

  const allModelOptions = useMemo(
    () => prepareModelList(modelos) as ModelTypeSelectI[],
    [modelos, modelTypeFilter],
  );

  const myModelOptions = useMemo(
    () => prepareModelListUser(modelos) as ModelTypeSelectI[],
    [modelos, modelTypeFilter],
  );

  const modelIdPrioritario =
    selectedMyModelId !== null ? selectedMyModelId : selectedAllModelId;

  const selectedAllOption =
    selectedAllModelId !== null
      ? allModelOptions.find((o) => o.value === selectedAllModelId)
      : undefined;

  const selectedMyOption =
    selectedMyModelId !== null
      ? myModelOptions.find((o) => o.value === selectedMyModelId)
      : undefined;

  const fallbackCurrModelOption = useMemo(() => {
    if (!currModel?.id) return undefined;
    return {
      value: currModel.id,
      label: `${currModel.txTipoDocumento} - ${currModel.txDescricao}`,
    } as ModelTypeSelectI;
  }, [currModel?.id, currModel?.txTipoDocumento, currModel?.txDescricao]);

  const selectedAllOptionResolved =
    selectedAllOption ?? (selectedAllModelId === currModel?.id ? fallbackCurrModelOption : undefined);

  const selectedMyOptionResolved =
    selectedMyOption ?? (selectedMyModelId === currModel?.id ? fallbackCurrModelOption : undefined);

  useEffect(() => {
    if (!currModel) return;
    updateText(desformatarPeca(currModel.txModeloPeca ?? ""));
  }, [currModel]);

  useEffect(() => {
    if (!currModel?.id) return;
    if (hasModelSelectionInteraction) return;

    const id = currModel.id;
    const existsInMy = myModelOptions.some((o) => o.value === id);
    const existsInAll = allModelOptions.some((o) => o.value === id);

    if (existsInMy || (!existsInAll && selectedMyModelId === null)) {
      setSelectedMyModelId(id);
      setSelectedAllModelId(null);
    } else if (existsInAll) {
      setSelectedAllModelId(id);
      setSelectedMyModelId(null);
    }
  }, [
    currModel?.id,
    hasModelSelectionInteraction,
    myModelOptions,
    allModelOptions,
    selectedMyModelId,
  ]);

  useEffect(() => {
    if (!hasModelSelectionInteraction) return;

    if (modelIdPrioritario === null) {
      modeloSelecionadoAplicadoRef.current = null;
      updateCurrModel(undefined as any);
      updateText("");
      return;
    }

    if (modeloSelecionadoAplicadoRef.current === modelIdPrioritario) return;

    const model = modelos?.find((m) => m.id === modelIdPrioritario);
    if (!model) return;

    let cancelled = false;
    const textAntesDaBusca = textRef.current;
    modeloSelecionadoAplicadoRef.current = modelIdPrioritario;

    getModeloById({ id: model.id }).then((m) => {
      if (cancelled) return;
      if (textRef.current !== textAntesDaBusca) return;
      updateText(desformatarPeca(m?.txModeloPeca ?? ""));
      updateCurrModel(m);
    });

    return () => {
      cancelled = true;
    };
  }, [modelIdPrioritario, modelos, hasModelSelectionInteraction]);

  const { idModelo } = useParams();

  useEffect(() => {
    if (!idModelo) return;
    if (modeloRotaAplicadoRef.current) return;
    if (recPeca === undefined) return;
    if (recPeca !== "NotFound") return;
    if (finlPeca) return;

    modeloRotaAplicadoRef.current = true;

    getModeloById({ id: parseInt(idModelo) }).then((m) => {
      updateText(desformatarPeca(m?.txModeloPeca ?? ""));
      updateCurrModel(m);
    });
  }, [idModelo, recPeca, finlPeca]);

  return (
    <S.DatasWrapper>
      <S.DataWrapper>
        <S.DataLabel>Todos os Modelos</S.DataLabel>
        <S.SelectData
          isDisabled={modelos?.length == 0}
          value={selectedAllOptionResolved}
          onChange={async (e: any) => {
            setHasModelSelectionInteraction(true);

            if (!e || e.value === -1) {
              setSelectedAllModelId(null);
              return;
            }

            setSelectedMyModelId(null);
            setSelectedAllModelId(e.value);
          }}
          options={allModelOptions}
        />
        {loadingModelosPecas && (
          <JvrisLoading size="small" loading={loadingModelosPecas} />
        )}
      </S.DataWrapper>

      <S.DataWrapper>
        <S.DataLabel>Meus Modelos</S.DataLabel>
        <S.SelectData
          isDisabled={modelos?.length == 0}
          value={selectedMyOptionResolved}
          onChange={async (e: any) => {
            setHasModelSelectionInteraction(true);

            if (!e || e.value === -1) {
              setSelectedMyModelId(null);
              return;
            }

            setSelectedAllModelId(null);
            setSelectedMyModelId(e.value);
          }}
          options={myModelOptions}
        />
        {loadingModelosPecas && (
          <JvrisLoading size="small" loading={loadingModelosPecas} />
        )}
      </S.DataWrapper>

      <S.DataWrapper>
        <S.DataLabel>Tipo de documento</S.DataLabel>
        <S.InputWrapper>
          <S.SelectData
            value={modelTypeFilter.value != -1 ? modelTypeFilter : undefined}
            onInputChange={(e) => {
              if (e.length) {
                updateModelTypeFilterValue({
                  value: 0,
                  label: e,
                });
              }
            }}
            onChange={(e) => {
              if (e) {
                updateModelTypeFilterValue(e);
                if (e.value != -1) updateTitle(e.label);
              }
            }}
            options={prepareModelTypeList(tiposProcessoPJE)}
          />
          {loadingTiposProcessoPJE && (
            <JvrisLoading size="small" loading={loadingTiposProcessoPJE} />
          )}
        </S.InputWrapper>
      </S.DataWrapper>

      <S.DataWrapper>
        <S.DataLabel>Titulo do documento</S.DataLabel>
        <S.TitleInput
          value={modelTitle}
          onChange={(e) => {
            updateTitle(e.target.value);
          }}
        />
      </S.DataWrapper>
    </S.DatasWrapper>
  );
};

export default InputsSection;
